import React, { useState, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import {
  Save,
  RotateCcw,
  Building2,
  ShieldCheck,
  Clock,
  Palette,
  Trash2,
  GraduationCap,
  WalletCards,
  Paintbrush,
  CalendarDays,
} from "lucide-react";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { systemService } from "../../../services/systemService";
import { getActiveEnrollmentContext } from "../../../services/enrollmentService";
import { academicCalendarService } from "../../../services/academicCalendarService";
import { summativeAssessmentTimetableService } from "../../../services/summativeAssessmentTimetableService";
import { useLocation } from "react-router-dom";
import SystemCalendarSettings from "./SystemCalendarSettings";

const UNIT_TYPES = ["core", "basic", "common"];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toDateInput = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Booking windows are selected as dates. Opening is the start of that date;
// closing is the final instant of the selected closing date (midnight rolls
// over immediately after it), so a configured date is fully bookable.
const bookingDateToIso = (value, closing = false) => {
  if (!value) return null;
  const date = new Date(`${value}T${closing ? "23:59:59.999" : "00:00:00"}`);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const normaliseInstitution = (data) => ({
  ...DEFAULT_INSTITUTION,
  ...data,
  reporting_opens_at: toDateInput(data?.reporting_opens_at),
  reporting_closes_at: toDateInput(data?.reporting_closes_at),
  registration_opens_at: toDateInput(data?.registration_opens_at),
  registration_closes_at: toDateInput(data?.registration_closes_at),
  late_registration_closes_at: toDateInput(data?.late_registration_closes_at),
  summative_exam_booking_opens_at: toDateTimeLocal(
    data?.summative_exam_booking_opens_at,
  ),
  summative_exam_booking_closes_at: toDateTimeLocal(
    data?.summative_exam_booking_closes_at,
  ),
  max_formative_failed_units_for_progression: Number.isFinite(
    Number(data?.max_formative_failed_units_for_progression),
  )
    ? Math.max(
        0,
        Math.floor(Number(data.max_formative_failed_units_for_progression)),
      )
    : 0,
  max_summative_failed_units_for_progression: Number.isFinite(
    Number(data?.max_summative_failed_units_for_progression),
  )
    ? Math.max(
        0,
        Math.floor(Number(data.max_summative_failed_units_for_progression)),
      )
    : 0,
  minimum_attendance_percentage_for_progression: Number.isFinite(
    Number(data?.minimum_attendance_percentage_for_progression),
  )
    ? Math.min(
        100,
        Math.max(0, Number(data.minimum_attendance_percentage_for_progression)),
      )
    : 70,
});

const DEFAULT_SUMMATIVE_TIMETABLE = {
  assessment_starts_at: "08:00",
  assessment_ends_at: "17:00",
  break_starts_at: "",
  break_ends_at: "",
  assessment_starts_on: "",
  assessment_ends_on: "",
  practical_assessment_starts_on: "",
};

const ENROLLMENT_WINDOW_FIELDS = [
  "reporting_opens_at",
  "reporting_closes_at",
  "registration_opens_at",
  "registration_closes_at",
  "late_registration_closes_at",
];

const SETTINGS_NAVIGATION = [
  {
    id: "profile",
    label: "Institution",
    shortLabel: "Institution",
    description: "Identity, contacts and official documents",
    icon: Building2,
  },
  {
    id: "enrolment",
    label: "Enrolment",
    shortLabel: "Enrolment",
    description: "Registration, progression and late fees",
    icon: GraduationCap,
  },
  {
    id: "calendar",
    label: "Calendar",
    shortLabel: "Calendar",
    description: "Academic years, semesters and events",
    icon: CalendarDays,
  },
  {
    id: "timetable",
    label: "Timetable",
    shortLabel: "Timetable",
    description: "Teaching days and automatic scheduling",
    icon: Clock,
  },
  {
    id: "finances",
    label: "Billing",
    shortLabel: "Billing",
    description: "Fees, billing mode and statutory charges",
    icon: WalletCards,
  },
  {
    id: "theme",
    label: "Appearance",
    shortLabel: "Appearance",
    description: "Application colours and typography",
    icon: Paintbrush,
  },
];

const DEFAULT_INSTITUTION = {
  id: null,
  name: "",
  motto: "",
  initials: "",
  address: "",
  phone: "",
  email: "",
  website: "",
  logo_url: "",
  postal_address: "",
  admissions_email: "",
  admissions_phone: "",
  admissions_office_name: "",
  registrar_name: "",
  registrar_title: "",
  registrar_signature_url: "",
  official_reference_prefix: "",
  admission_letter_footer: "",
  student_email_domain: "",
  staff_email_domain: "",
  default_country: "Kenya",
  default_nationality: "Kenyan",
  timezone: "Africa/Nairobi",
  locale: "en-KE",
  currency_code: "KES",
  date_format: "dd/MM/yyyy",
  enrollment_controls_enabled: true,
  require_configured_enrollment_windows: true,
  require_intake_eligibility: true,
  allow_late_reporting: true,
  allow_late_unit_registration: true,
  default_late_reporting_fee: 0,
  default_late_unit_registration_fee: 0,
  default_reactivation_fee: 0,
  max_formative_failed_units_for_progression: 0,
  max_summative_failed_units_for_progression: 0,
  minimum_attendance_percentage_for_progression: 70,
  reporting_opens_at: "",
  reporting_closes_at: "",
  registration_opens_at: "",
  registration_closes_at: "",
  late_registration_closes_at: "",
  enrollment_window_academic_year_id: null,
  enrollment_window_semester_id: null,
  billing_mode: "program_based",
  statutory_fees_config: [],
  default_school_fee: 0,
  attachment_fee: 8250,
  summative_exam_booking_enabled: true,
  summative_exam_booking_opens_at: "",
  summative_exam_booking_closes_at: "",
  timetable_day_starts_at: "07:00",
  timetable_day_ends_at: "20:00",
  timetable_break_starts_at: "",
  timetable_break_ends_at: "",
  timetable_working_days: [1, 2, 3, 4, 5],
};

const SettingsPanel = ({
  id,
  title,
  subtitle,
  icon: Icon,
  badge,
  onReset,
  headerActions,
  children,
}) => {
  return (
    <section
      id={`section-${id}`}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 bg-slate-50/70 px-5 py-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {Icon && (
            <div className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-700 shadow-sm">
              <Icon className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1 lg:flex-nowrap">
              <h2 className="text-base font-bold tracking-tight text-slate-950">
                {title}
              </h2>
              {badge && (
                <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-slate-500">
                  {badge}
                </span>
              )}
              <p className="min-w-0 truncate text-xs text-slate-500">
                {subtitle}
              </p>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {headerActions}
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset this section
            </button>
          )}
        </div>
      </div>
      <div className="p-5 md:p-6">{children}</div>
    </section>
  );
};

const SystemSettings = () => {
  const {
    theme,
    updateTheme,
    resetTheme,
    saveThemeToDB,
    updateInstitutionSettings,
  } = useTheme();
  const location = useLocation();
  const [institution, setInstitution] = useState(DEFAULT_INSTITUTION);

  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [unitTypeFees, setUnitTypeFees] = useState({});
  const [activeEnrollmentContext, setActiveEnrollmentContext] = useState(null);
  const [summativeTimetable, setSummativeTimetable] = useState(
    DEFAULT_SUMMATIVE_TIMETABLE,
  );
  const [summativeTimetableDirty, setSummativeTimetableDirty] = useState(false);
  const [summativeTimetableLoading, setSummativeTimetableLoading] =
    useState(false);
  const [bookingPeriods, setBookingPeriods] = useState([]);
  const [bookingWindowPeriod, setBookingWindowPeriod] = useState({
    academicYearId: "",
    semesterId: "",
  });
  const [bookingWindow, setBookingWindow] = useState({
    configured: false,
    booking_enabled: true,
    opens_at: "",
    closes_at: "",
    status: "unconfigured",
    can_book: false,
  });
  const [bookingWindowLoading, setBookingWindowLoading] = useState(false);
  const [bookingWindowSaving, setBookingWindowSaving] = useState(false);
  const [bookingWindowDirty, setBookingWindowDirty] = useState(false);

  const [activeSection, setActiveSection] = useState(
    location.state?.section || "profile",
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [themeDirty, setThemeDirty] = useState(false);

  useEffect(() => {
    const requestedSection = location.state?.section;
    if (
      requestedSection &&
      SETTINGS_NAVIGATION.some((item) => item.id === requestedSection)
    ) {
      setActiveSection(requestedSection);
    }
  }, [location.state?.section]);

  const loadSummativeTimetable = async (context) => {
    const academicYearId = context?.academicYear?.id;
    const semesterId = context?.semester?.id;
    if (!academicYearId || !semesterId) {
      setSummativeTimetable(DEFAULT_SUMMATIVE_TIMETABLE);
      setSummativeTimetableDirty(false);
      return;
    }
    setSummativeTimetableLoading(true);
    try {
      const data = await summativeAssessmentTimetableService.getSettings(
        academicYearId,
        semesterId,
      );
      setSummativeTimetable({
        ...DEFAULT_SUMMATIVE_TIMETABLE,
        ...(data || {}),
        assessment_starts_at: String(
          data?.assessment_starts_at ||
            DEFAULT_SUMMATIVE_TIMETABLE.assessment_starts_at,
        ).slice(0, 5),
        assessment_ends_at: String(
          data?.assessment_ends_at ||
            DEFAULT_SUMMATIVE_TIMETABLE.assessment_ends_at,
        ).slice(0, 5),
        break_starts_at: String(data?.break_starts_at || "").slice(0, 5),
        break_ends_at: String(data?.break_ends_at || "").slice(0, 5),
        assessment_starts_on: toDateInput(data?.assessment_starts_on),
        assessment_ends_on: toDateInput(data?.assessment_ends_on),
        practical_assessment_starts_on: toDateInput(
          data?.practical_assessment_starts_on || data?.assessment_starts_on,
        ),
      });
      setSummativeTimetableDirty(false);
    } catch (error) {
      setSummativeTimetable({ ...DEFAULT_SUMMATIVE_TIMETABLE });
      setSummativeTimetableDirty(false);
      setSaveMessage({
        type: "warning",
        text:
          error?.message ||
          "Summative assessment timetable settings could not be loaded.",
      });
    } finally {
      setSummativeTimetableLoading(false);
    }
  };

  useEffect(() => {
    loadInstitutionDetails();
  }, []);

  useEffect(() => {
    if (activeSection !== "enrolment") return undefined;
    loadInstitutionDetails();
    return undefined;
  }, [activeSection]);

  const loadBookingWindow = async (
    academicYearId,
    semesterId,
    fallback = null,
  ) => {
    if (!academicYearId || !semesterId) {
      setBookingWindow({
        configured: false,
        booking_enabled: true,
        opens_at: "",
        closes_at: "",
        status: "unconfigured",
        can_book: false,
      });
      setBookingWindowDirty(false);
      return;
    }
    setBookingWindowLoading(true);
    try {
      const data = await systemService.getSummativeExamBookingWindow(
        academicYearId,
        semesterId,
      );
      setBookingWindow({
        ...data,
        opens_at: toDateInput(data?.opens_at),
        closes_at: toDateInput(data?.closes_at),
      });
      setBookingWindowDirty(false);
    } catch (error) {
      // Older deployments can still display their legacy institution
      // window until migration 00093 is applied.
      const legacy = fallback || {};
      setBookingWindow({
        configured: false,
        booking_enabled: legacy.summative_exam_booking_enabled !== false,
        opens_at: toDateInput(legacy.summative_exam_booking_opens_at),
        closes_at: toDateInput(legacy.summative_exam_booking_closes_at),
        status: "legacy",
        can_book: true,
      });
      setSaveMessage({
        type: "warning",
        text:
          error?.message ||
          "Period-specific booking settings are unavailable until migration 20260101000093 is applied.",
      });
    } finally {
      setBookingWindowLoading(false);
    }
  };

  const loadBookingPeriods = async (context, fallback = null) => {
    try {
      const years = await academicCalendarService.getAcademicYears();
      const semesterGroups = await Promise.all(
        (years || []).map(async (year) => ({
          year,
          semesters: await academicCalendarService.getSemesters(year.id),
        })),
      );
      const periods = semesterGroups.flatMap(({ year, semesters }) =>
        (semesters || []).map((semester) => ({ year, semester })),
      );
      setBookingPeriods(periods);
      if (!periods.length) {
        setBookingWindowPeriod({ academicYearId: "", semesterId: "" });
        setBookingWindow({
          configured: false,
          booking_enabled: true,
          opens_at: "",
          closes_at: "",
          status: "unconfigured",
          can_book: false,
        });
        return;
      }
      const activeYearId = context?.academicYear?.id;
      const activeSemesterId = context?.semester?.id;
      const preferred =
        periods.find(
          (period) =>
            period.year.id === activeYearId &&
            period.semester.id === activeSemesterId,
        ) ||
        periods.find(
          (period) =>
            String(period.semester.status || "").toLowerCase() === "active" &&
            String(period.year.status || "").toLowerCase() === "active",
        ) ||
        periods[0];
      setBookingWindowPeriod({
        academicYearId: preferred.year.id,
        semesterId: preferred.semester.id,
      });
      await loadBookingWindow(
        preferred.year.id,
        preferred.semester.id,
        fallback,
      );
    } catch (error) {
      console.warn(
        "Academic periods for examination booking settings could not be loaded:",
        error,
      );
      setBookingPeriods([]);
      setBookingWindowPeriod({ academicYearId: "", semesterId: "" });
      setSaveMessage({
        type: "warning",
        text: "Academic periods could not be loaded. Create an academic year and semester in Calendar first.",
      });
    }
  };

  const loadInstitutionDetails = async () => {
    setLoading(true);
    try {
      const [data, contextResult] = await Promise.all([
        systemService.getInstitutionDetails(),
        getActiveEnrollmentContext(),
      ]);
      const context = contextResult?.success ? contextResult.data : null;
      setActiveEnrollmentContext(context);
      await loadSummativeTimetable(context);

      if (data) {
        const normalized = normaliseInstitution(data);
        const windowBelongsToAnotherPeriod = Boolean(
          normalized.enrollment_window_semester_id &&
          normalized.enrollment_window_semester_id !== context?.semester?.id,
        );
        const periodScopedInstitution = windowBelongsToAnotherPeriod
          ? {
              ...normalized,
              ...Object.fromEntries(
                ENROLLMENT_WINDOW_FIELDS.map((field) => [field, ""]),
              ),
              default_late_reporting_fee: 0,
              default_late_unit_registration_fee: 0,
            }
          : normalized;
        setInstitution(periodScopedInstitution);
        updateInstitutionSettings(data);
        if (data.unit_type_fees) {
          try {
            const parsed =
              typeof data.unit_type_fees === "string"
                ? JSON.parse(data.unit_type_fees)
                : data.unit_type_fees;
            setUnitTypeFees(parsed || {});
          } catch (e) {
            console.warn(e);
          }
        }
        await loadBookingPeriods(context, normalized);
      } else {
        await loadBookingPeriods(context);
      }
      if (!contextResult?.success) {
        console.warn(
          "Active enrollment period could not be loaded:",
          contextResult?.error,
        );
      }
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error(error);
      setSaveMessage({
        type: "error",
        text: "Unable to load system settings. Refresh and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInstitution((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setHasUnsavedChanges(true);
  };

  const handleSummativeTimetableChange = (field, value) => {
    setSummativeTimetable((prev) => ({ ...prev, [field]: value }));
    setSummativeTimetableDirty(true);
  };

  const handleBookingWindowChange = (field, value) => {
    setBookingWindow((prev) => ({
      ...prev,
      [field]: value,
      status: "unsaved",
    }));
    setBookingWindowDirty(true);
  };

  const handleBookingYearChange = async (event) => {
    const academicYearId = event.target.value;
    const matching = bookingPeriods
      .filter((period) => period.year.id === academicYearId)
      .sort(
        (a, b) => (a.semester.term_number || 0) - (b.semester.term_number || 0),
      )[0];
    const semesterId = matching?.semester?.id || "";
    setBookingWindowPeriod({ academicYearId, semesterId });
    await loadBookingWindow(academicYearId, semesterId, institution);
  };

  const handleBookingSemesterChange = async (event) => {
    const semesterId = event.target.value;
    setBookingWindowPeriod((prev) => ({ ...prev, semesterId }));
    await loadBookingWindow(
      bookingWindowPeriod.academicYearId,
      semesterId,
      institution,
    );
  };

  const handleUnitTypeFeeChange = (type, category, value) => {
    setUnitTypeFees((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || {}),
        [category]: value,
      },
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddStatutoryFee = () => {
    setInstitution((prev) => ({
      ...prev,
      statutory_fees_config: [
        ...(prev.statutory_fees_config || []),
        { name: "", amount: 0, modules: [1] },
      ],
    }));
    setHasUnsavedChanges(true);
  };

  const handleRemoveStatutoryFee = (index) => {
    setInstitution((prev) => {
      const newConfig = [...(prev.statutory_fees_config || [])];
      newConfig.splice(index, 1);
      return { ...prev, statutory_fees_config: newConfig };
    });
    setHasUnsavedChanges(true);
  };

  const handleUpdateStatutoryFee = (index, field, value) => {
    setInstitution((prev) => {
      const newConfig = [...(prev.statutory_fees_config || [])];
      newConfig[index] = { ...newConfig[index], [field]: value };
      return { ...prev, statutory_fees_config: newConfig };
    });
    setHasUnsavedChanges(true);
  };

  const handleToggleStatutoryFeeModule = (index, moduleNum) => {
    setInstitution((prev) => {
      const newConfig = [...(prev.statutory_fees_config || [])];
      const currentModules = newConfig[index].modules || [];
      if (currentModules.includes(moduleNum)) {
        newConfig[index].modules = currentModules.filter(
          (m) => m !== moduleNum,
        );
      } else {
        newConfig[index].modules = [...currentModules, moduleNum].sort();
      }
      return { ...prev, statutory_fees_config: newConfig };
    });
    setHasUnsavedChanges(true);
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setHasUnsavedChanges(true);
    }
  };

  const toggleTimetableDay = (day) => {
    setInstitution((prev) => {
      const current = Array.isArray(prev.timetable_working_days)
        ? prev.timetable_working_days.map(Number)
        : [1, 2, 3, 4, 5];
      return {
        ...prev,
        timetable_working_days: current.includes(day)
          ? current.filter((item) => item !== day)
          : [...current, day].sort(),
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleResetSection = (sectionKey) => {
    if (sectionKey === "profile") {
      if (window.confirm("Reset unsaved institution profile changes?")) {
        loadInstitutionDetails();
      }
    } else if (sectionKey === "enrolment") {
      if (window.confirm("Reset enrolment governance settings to defaults?")) {
        setInstitution((prev) => ({
          ...prev,
          enrollment_controls_enabled: true,
          require_configured_enrollment_windows: true,
          require_intake_eligibility: true,
          allow_late_reporting: true,
          allow_late_unit_registration: true,
          default_late_reporting_fee: 0,
          default_late_unit_registration_fee: 0,
          default_reactivation_fee: 0,
          max_formative_failed_units_for_progression: 0,
          max_summative_failed_units_for_progression: 0,
          minimum_attendance_percentage_for_progression: 70,
          reporting_opens_at: "",
          reporting_closes_at: "",
          registration_opens_at: "",
          registration_closes_at: "",
          late_registration_closes_at: "",
        }));
        setHasUnsavedChanges(true);
      }
    } else if (sectionKey === "timetable") {
      if (
        window.confirm(
          "Reset timetable rules to defaults (07:00 - 20:00, Mon-Fri)?",
        )
      ) {
        setInstitution((prev) => ({
          ...prev,
          timetable_day_starts_at: "07:00",
          timetable_day_ends_at: "20:00",
          timetable_break_starts_at: "",
          timetable_break_ends_at: "",
          timetable_working_days: [1, 2, 3, 4, 5],
        }));
        setSummativeTimetable({ ...DEFAULT_SUMMATIVE_TIMETABLE });
        setSummativeTimetableDirty(true);
        setHasUnsavedChanges(true);
      }
    } else if (sectionKey === "finances") {
      if (
        window.confirm(
          "Reset unsaved billing defaults, unit type fees, and statutory fees?",
        )
      ) {
        setInstitution((prev) => ({
          ...prev,
          billing_mode: DEFAULT_INSTITUTION.billing_mode,
          statutory_fees_config: [],
          default_school_fee: DEFAULT_INSTITUTION.default_school_fee,
          attachment_fee: DEFAULT_INSTITUTION.attachment_fee,
        }));
        setUnitTypeFees({});
        setBookingWindow((prev) => ({
          ...prev,
          booking_enabled: true,
          opens_at: "",
          closes_at: "",
          status: "unsaved",
        }));
        setBookingWindowDirty(true);
        setHasUnsavedChanges(true);
      }
    } else if (sectionKey === "theme") {
      if (window.confirm("Reset theme customization to system defaults?")) {
        resetTheme();
        setThemeDirty(true);
      }
    }
  };

  const handleThemeChange = (key, value) => {
    updateTheme(key, value);
    setThemeDirty(true);
  };

  const handleTestStorage = async () => {
    try {
      const { data, error } = await systemService.listBuckets();
      if (error) {
        setSaveMessage({
          type: "error",
          text: `Storage connection failed: ${error.message}`,
        });
      } else {
        const names = (data || []).map((bucket) => bucket.name).join(", ");
        setSaveMessage({
          type: "success",
          text: `Storage connection is working${names ? `: ${names}` : "."}`,
        });
      }
    } catch (err) {
      setSaveMessage({
        type: "error",
        text: `Storage connection failed: ${err.message}`,
      });
    }
  };

  const handleSaveInstitution = async () => {
    const activeAcademicYear = activeEnrollmentContext?.academicYear;
    const activeSemester = activeEnrollmentContext?.semester;
    const hasActiveEnrollmentPeriod = Boolean(
      activeAcademicYear?.id && activeSemester?.id,
    );
    const hasEnrollmentWindowValues =
      ENROLLMENT_WINDOW_FIELDS.some((field) =>
        String(institution[field] || "").trim(),
      ) ||
      [
        "default_late_reporting_fee",
        "default_late_unit_registration_fee",
        "default_reactivation_fee",
      ].some((field) => Number(institution[field] || 0) > 0);
    if (
      activeSection === "enrolment" &&
      !hasActiveEnrollmentPeriod &&
      hasEnrollmentWindowValues
    ) {
      setSaveMessage({
        type: "error",
        text: "Set an active academic year and semester in System Settings → Calendar before configuring enrollment dates.",
      });
      return;
    }

    const dayStart = String(institution.timetable_day_starts_at || "");
    const dayEnd = String(institution.timetable_day_ends_at || "");
    const breakStart = String(institution.timetable_break_starts_at || "");
    const breakEnd = String(institution.timetable_break_ends_at || "");
    const workingDays = Array.isArray(institution.timetable_working_days)
      ? institution.timetable_working_days
      : [];

    if (dayStart && dayEnd && dayStart >= dayEnd) {
      setSaveMessage({
        type: "error",
        text: "Teaching day end time must be later than the start time.",
      });
      return;
    }
    if ((breakStart && !breakEnd) || (!breakStart && breakEnd)) {
      setSaveMessage({
        type: "error",
        text: "Set both global break times, or leave both blank.",
      });
      return;
    }
    if (breakStart && breakEnd && breakStart >= breakEnd) {
      setSaveMessage({
        type: "error",
        text: "Global break end time must be later than its start time.",
      });
      return;
    }
    if (!workingDays.length) {
      setSaveMessage({
        type: "error",
        text: "Select at least one active teaching day.",
      });
      return;
    }

    const reportingOpens = String(institution.reporting_opens_at || "").trim();
    const reportingCloses = String(
      institution.reporting_closes_at || "",
    ).trim();
    const registrationOpens = String(
      institution.registration_opens_at || "",
    ).trim();
    const registrationCloses = String(
      institution.registration_closes_at || "",
    ).trim();
    const lateRegistrationCloses = String(
      institution.late_registration_closes_at || "",
    ).trim();
    if (reportingOpens && reportingCloses && reportingOpens > reportingCloses) {
      setSaveMessage({
        type: "error",
        text: "Reporting close date must be on or after the reporting open date.",
      });
      return;
    }
    if (
      registrationOpens &&
      registrationCloses &&
      registrationOpens > registrationCloses
    ) {
      setSaveMessage({
        type: "error",
        text: "Unit-registration close date must be on or after the unit-registration open date.",
      });
      return;
    }
    if (
      lateRegistrationCloses &&
      ((reportingCloses && lateRegistrationCloses < reportingCloses) ||
        (registrationCloses && lateRegistrationCloses < registrationCloses))
    ) {
      setSaveMessage({
        type: "error",
        text: "Late grace close date must be on or after both normal close dates.",
      });
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    try {
      let logoUrl = institution.logo_url;

      if (logoFile) {
        const upload = await systemService.uploadLogo(logoFile);
        if (upload.success) {
          logoUrl = upload.url;
        } else {
          console.error("Upload failed:", upload.error);
          setSaveMessage({
            type: "warning",
            text: `Logo upload failed: ${upload.error?.message || "Unknown error"}. Other details were saved.`,
          });
        }
      }

      const cleanDomain = (value) =>
        String(value || "")
          .trim()
          .replace(/^@+/, "")
          .toLowerCase();
      const scopedWindowUpdates =
        activeSection === "enrolment" && hasActiveEnrollmentPeriod
          ? {
              enrollment_window_academic_year_id: activeAcademicYear.id,
              enrollment_window_semester_id: activeSemester.id,
            }
          : {};
      const {
        reporting_opens_at: _reportingOpensAt,
        reporting_closes_at: _reportingClosesAt,
        registration_opens_at: _registrationOpensAt,
        registration_closes_at: _registrationClosesAt,
        late_registration_closes_at: _lateRegistrationClosesAt,
        enrollment_window_academic_year_id: _windowAcademicYearId,
        enrollment_window_semester_id: _windowSemesterId,
        default_late_reporting_fee: _defaultLateReportingFee,
        default_late_unit_registration_fee: _defaultLateUnitRegistrationFee,
        default_reactivation_fee: _defaultReactivationFee,
        summative_exam_booking_enabled: _legacyBookingEnabled,
        summative_exam_booking_opens_at: _legacyBookingOpensAt,
        summative_exam_booking_closes_at: _legacyBookingClosesAt,
        ...institutionDetails
      } = institution;
      const updates = {
        ...institutionDetails,
        ...scopedWindowUpdates,
        logo_url: logoUrl,
        unit_type_fees: unitTypeFees,
        student_email_domain: cleanDomain(institution.student_email_domain),
        staff_email_domain: cleanDomain(institution.staff_email_domain),
        timetable_break_starts_at:
          institution.timetable_break_starts_at || null,
        timetable_break_ends_at: institution.timetable_break_ends_at || null,
      };
      if (activeSection === "enrolment") {
        Object.assign(updates, {
          reporting_opens_at: reportingOpens || null,
          reporting_closes_at: reportingCloses || null,
          registration_opens_at: registrationOpens || null,
          registration_closes_at: registrationCloses || null,
          late_registration_closes_at: lateRegistrationCloses || null,
          default_late_reporting_fee: Number(
            institution.default_late_reporting_fee || 0,
          ),
          default_late_unit_registration_fee: Number(
            institution.default_late_unit_registration_fee || 0,
          ),
          default_reactivation_fee: Number(
            institution.default_reactivation_fee || 0,
          ),
          max_formative_failed_units_for_progression: Math.max(
            0,
            Math.floor(
              Number(
                institution.max_formative_failed_units_for_progression || 0,
              ),
            ),
          ),
          max_summative_failed_units_for_progression: Math.max(
            0,
            Math.floor(
              Number(
                institution.max_summative_failed_units_for_progression || 0,
              ),
            ),
          ),
          minimum_attendance_percentage_for_progression: Math.min(
            100,
            Math.max(
              0,
              Number(
                institution.minimum_attendance_percentage_for_progression ?? 70,
              ),
            ),
          ),
        });
      }
      const result = await systemService.updateInstitutionDetails(
        institution.id,
        updates,
      );

      if (result.success) {
        setInstitution(normaliseInstitution(result.data));
        updateInstitutionSettings(result.data);
        let saveNotice = {
          type: "success",
          text: "System settings saved successfully.",
        };
        if (activeSection === "enrolment") {
          const recalculation =
            await systemService.recalculateAllStudentModuleProgression();
          if (recalculation.success) {
            const summary = recalculation.data || {};
            const processed = Number(summary.processed || 0);
            const advanced = Number(summary.advanced || 0);
            const failed = Number(summary.failed || 0);
            saveNotice = {
              type: failed > 0 ? "warning" : "success",
              text: `System settings saved. Progression checked for ${processed} student${processed === 1 ? "" : "s"}; ${advanced} advanced${failed > 0 ? `, ${failed} could not be checked` : ""}.`,
            };
          } else {
            saveNotice = {
              type: "warning",
              text: `System settings saved, but existing student progression could not be recalculated: ${recalculation.message || "apply the progression migrations and try again."}`,
            };
          }
        }
        setSaveMessage(saveNotice);
        setLogoFile(null);
        setHasUnsavedChanges(false);
      } else {
        setSaveMessage({
          type: "error",
          text:
            result.message ||
            result.error?.message ||
            "Failed to save institution details.",
        });
        return false;
      }
    } catch (error) {
      console.error(error);
      setSaveMessage({
        type: "error",
        text: error?.message || "An unexpected error occurred while saving.",
      });
      return false;
    } finally {
      setSaving(false);
    }
    return true;
  };

  const handleSaveTimetable = async () => {
    const institutionSaved = await handleSaveInstitution();
    if (!institutionSaved) return false;

    const academicYearId = activeEnrollmentContext?.academicYear?.id;
    const semesterId = activeEnrollmentContext?.semester?.id;
    if (!academicYearId || !semesterId) {
      if (summativeTimetableDirty) {
        setSaveMessage({
          type: "error",
          text: "Set an active academic year and semester in Calendar before saving the summative assessment window.",
        });
        return false;
      }
      return true;
    }

    const assessmentStartsAt = String(
      summativeTimetable.assessment_starts_at || "",
    ).trim();
    const assessmentEndsAt = String(
      summativeTimetable.assessment_ends_at || "",
    ).trim();
    const breakStartsAt = String(
      summativeTimetable.break_starts_at || "",
    ).trim();
    const breakEndsAt = String(summativeTimetable.break_ends_at || "").trim();
    const periodStartsOn = String(
      summativeTimetable.assessment_starts_on || "",
    ).trim();
    const periodEndsOn = String(
      summativeTimetable.assessment_ends_on || "",
    ).trim();
    const practicalStartsOn = String(
      summativeTimetable.practical_assessment_starts_on || periodStartsOn,
    ).trim();
    if (
      !assessmentStartsAt ||
      !assessmentEndsAt ||
      assessmentStartsAt >= assessmentEndsAt
    ) {
      setSaveMessage({
        type: "error",
        text: "Assessment day end time must be later than its start time.",
      });
      return false;
    }
    if ((breakStartsAt && !breakEndsAt) || (!breakStartsAt && breakEndsAt)) {
      setSaveMessage({
        type: "error",
        text: "Set both assessment break times, or leave both blank.",
      });
      return false;
    }
    if (
      breakStartsAt &&
      (breakStartsAt >= breakEndsAt ||
        breakStartsAt < assessmentStartsAt ||
        breakEndsAt > assessmentEndsAt)
    ) {
      setSaveMessage({
        type: "error",
        text: "Assessment break must sit inside the assessment day.",
      });
      return false;
    }
    if (!periodStartsOn || !periodEndsOn || periodStartsOn > periodEndsOn) {
      setSaveMessage({
        type: "error",
        text: "Set a valid exam period start and end date.",
      });
      return false;
    }
    if (
      !practicalStartsOn ||
      practicalStartsOn < periodStartsOn ||
      practicalStartsOn > periodEndsOn
    ) {
      setSaveMessage({
        type: "error",
        text: "Practical assessment start date must be inside the exam period.",
      });
      return false;
    }
    setSaving(true);
    try {
      const saved = await summativeAssessmentTimetableService.saveSettings({
        academicYearId,
        semesterId,
        assessmentStartsAt,
        assessmentEndsAt,
        breakStartsAt,
        breakEndsAt,
        periodStartsOn,
        periodEndsOn,
        practicalStartsOn,
      });
      setSummativeTimetable((prev) => ({
        ...prev,
        ...saved,
        assessment_starts_at: String(
          saved?.assessment_starts_at || assessmentStartsAt,
        ).slice(0, 5),
        assessment_ends_at: String(
          saved?.assessment_ends_at || assessmentEndsAt,
        ).slice(0, 5),
        break_starts_at: String(saved?.break_starts_at || "").slice(0, 5),
        break_ends_at: String(saved?.break_ends_at || "").slice(0, 5),
        assessment_starts_on: toDateInput(saved?.assessment_starts_on),
        assessment_ends_on: toDateInput(saved?.assessment_ends_on),
        practical_assessment_starts_on: toDateInput(
          saved?.practical_assessment_starts_on || practicalStartsOn,
        ),
      }));
      setSummativeTimetableDirty(false);
      setSaveMessage({
        type: "success",
        text: "Teaching and summative assessment timetable settings saved for the active academic period.",
      });
      return true;
    } catch (error) {
      setSaveMessage({
        type: "error",
        text:
          error?.message ||
          "Summative assessment timetable settings could not be saved.",
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBookingWindow = async ({ quiet = false } = {}) => {
    const { academicYearId, semesterId } = bookingWindowPeriod;
    if (!academicYearId || !semesterId) {
      setSaveMessage({
        type: "error",
        text: "Select an academic year and semester before saving the examination booking window.",
      });
      return false;
    }
    const opensAt = String(bookingWindow.opens_at || "").trim();
    const closesAt = String(bookingWindow.closes_at || "").trim();
    if (opensAt && closesAt && opensAt > closesAt) {
      setSaveMessage({
        type: "error",
        text: "Booking close time must be later than the opening time.",
      });
      return false;
    }
    setBookingWindowSaving(true);
    if (!quiet) setSaveMessage(null);
    try {
      const saved = await systemService.saveSummativeExamBookingWindow({
        academicYearId,
        semesterId,
        bookingEnabled: bookingWindow.booking_enabled !== false,
        opensAt: bookingDateToIso(opensAt),
        closesAt: bookingDateToIso(closesAt, true),
      });
      setBookingWindow((prev) => ({
        ...prev,
        ...saved,
        opens_at: toDateInput(saved?.opens_at),
        closes_at: toDateInput(saved?.closes_at),
        status: "saved",
        configured: true,
      }));
      setBookingWindowDirty(false);
      if (!quiet)
        setSaveMessage({
          type: "success",
          text: "Examination booking window saved for the selected academic period.",
        });
      return true;
    } catch (error) {
      setSaveMessage({
        type: "error",
        text:
          error?.message || "Unable to save the examination booking window.",
      });
      return false;
    } finally {
      setBookingWindowSaving(false);
    }
  };

  const handleSaveFinance = async () => {
    const institutionSaved = await handleSaveInstitution();
    if (!institutionSaved) return;
    if (bookingWindowDirty) await handleSaveBookingWindow({ quiet: true });
    else
      setSaveMessage(
        (prev) =>
          prev || {
            type: "success",
            text: "Billing settings saved successfully.",
          },
      );
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const savedTheme = await saveThemeToDB({ silent: true });
      if (savedTheme) {
        setThemeDirty(false);
        setSaveMessage({
          type: "success",
          text: "Appearance settings saved successfully.",
        });
      } else {
        setSaveMessage({
          type: "error",
          text: "Unable to save appearance settings.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCurrentSection = () => {
    if (activeSection === "theme") return handleSaveTheme();
    if (activeSection === "calendar") return undefined;
    if (activeSection === "finances") return handleSaveFinance();
    if (activeSection === "timetable") return handleSaveTimetable();
    return handleSaveInstitution();
  };

  const hasPendingChanges =
    hasUnsavedChanges ||
    themeDirty ||
    bookingWindowDirty ||
    summativeTimetableDirty;
  const windowConfiguredForAnotherPeriod = Boolean(
    institution.enrollment_window_semester_id &&
    activeEnrollmentContext?.semester?.id &&
    institution.enrollment_window_semester_id !==
      activeEnrollmentContext.semester.id,
  );

  const settingsHeaderActions = (
    <>
      <span
        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${hasPendingChanges ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}
      >
        {hasPendingChanges ? "Unsaved changes" : "All changes saved"}
      </span>
      {activeSection === "calendar" ? (
        <span className="text-xs font-medium text-slate-500">
          Calendar changes save immediately
        </span>
      ) : (
        <button
          onClick={handleSaveCurrentSection}
          disabled={saving || loading || bookingWindowSaving}
          className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary px-3.5 text-xs font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {saving
            ? "Saving…"
            : activeSection === "theme"
              ? "Save appearance"
              : "Save changes"}
        </button>
      )}
    </>
  );

  return (
    <AdminLayout title="System Settings">
      <div className="space-y-5">
        {saveMessage && (
          <div
            role="status"
            className={`rounded-xl border p-3 text-xs font-medium flex items-center justify-between ${
              saveMessage.type === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : saveMessage.type === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-rose-200 bg-rose-50 text-rose-800"
            }`}
          >
            <span>{saveMessage.text}</span>
            <button
              onClick={() => setSaveMessage(null)}
              className="text-xs underline font-semibold"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* 1. Institution Profile Subsection */}
        {activeSection === "profile" && (
          <SettingsPanel
            id="profile"
            title="Institution Profile"
            subtitle="Manage your institution identity, contact details, registrar signature, and official letter headings."
            icon={Building2}
            badge="Identity & Contact"
            headerActions={settingsHeaderActions}
            onReset={() => handleResetSection("profile")}
          >
            {loading ? (
              <p className="text-xs text-muted-foreground p-4 text-center">
                Loading institution details...
              </p>
            ) : (
              <div className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-800">
                      Brand assets
                    </p>
                    <p className="mt-0.5 text-[0.6875rem] text-slate-500">
                      The logo is used in official documents and PDF timetables.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestStorage}
                    className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-100"
                  >
                    Test Storage Connection
                  </button>
                </div>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Identity & defaults
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        What the system displays and uses for new records.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Institution Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={institution.name}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="e.g. Eastwood Technical College"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Initials
                        </label>
                        <input
                          type="text"
                          name="initials"
                          value={institution.initials || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="e.g. ETC"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Motto
                      </label>
                      <input
                        type="text"
                        name="motto"
                        value={institution.motto || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="e.g. Excellence in Technical Education"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Default Country
                        </label>
                        <input
                          type="text"
                          name="default_country"
                          value={institution.default_country || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Default Nationality
                        </label>
                        <input
                          type="text"
                          name="default_nationality"
                          value={institution.default_nationality || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          System Timezone
                        </label>
                        <input
                          type="text"
                          name="timezone"
                          value={institution.timezone || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="Africa/Nairobi"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Locale
                        </label>
                        <input
                          type="text"
                          name="locale"
                          value={institution.locale || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="en-KE"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Currency Code
                        </label>
                        <input
                          type="text"
                          name="currency_code"
                          value={institution.currency_code || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="KES"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Date Format
                        </label>
                        <input
                          type="text"
                          name="date_format"
                          value={institution.date_format || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="dd/MM/yyyy"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Official Institution Logo
                      </label>
                      <div className="flex items-center gap-4 border p-3 rounded-lg bg-muted/20">
                        {institution.logo_url && (
                          <img
                            src={institution.logo_url}
                            alt="Logo"
                            className="h-10 w-10 object-contain border rounded bg-white p-1"
                          />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          className="text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-bold text-slate-900">
                        Contact & official documents
                      </h3>
                      <p className="mt-1 text-xs text-slate-500">
                        Addresses, admissions communications and letter
                        sign-off.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Physical Address
                      </label>
                      <textarea
                        name="address"
                        value={institution.address || ""}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="Physical Address"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Phone Number
                        </label>
                        <input
                          type="text"
                          name="phone"
                          value={institution.phone || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Official Email
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={institution.email || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Website URL
                      </label>
                      <input
                        type="url"
                        name="website"
                        value={institution.website || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="https://"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Postal Address
                      </label>
                      <input
                        type="text"
                        name="postal_address"
                        value={institution.postal_address || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="P.O. Box ..."
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Admissions Phone
                        </label>
                        <input
                          type="text"
                          name="admissions_phone"
                          value={institution.admissions_phone || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Admissions Email
                        </label>
                        <input
                          type="email"
                          name="admissions_email"
                          value={institution.admissions_email || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Student Email Domain
                        </label>
                        <input
                          type="text"
                          name="student_email_domain"
                          value={institution.student_email_domain || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="students.example.ac.ke"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Staff Email Domain
                        </label>
                        <input
                          type="text"
                          name="staff_email_domain"
                          value={institution.staff_email_domain || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                          placeholder="staff.example.ac.ke"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Official Reference Prefix
                      </label>
                      <input
                        type="text"
                        name="official_reference_prefix"
                        value={institution.official_reference_prefix || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="REG/ADM"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Admission Letter Office Heading
                      </label>
                      <input
                        type="text"
                        name="admissions_office_name"
                        value={institution.admissions_office_name || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="Office of the Registrar (Academic Affairs)"
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Registrar Name
                        </label>
                        <input
                          type="text"
                          name="registrar_name"
                          value={institution.registrar_name || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Registrar Title
                        </label>
                        <input
                          type="text"
                          name="registrar_title"
                          value={institution.registrar_title || ""}
                          onChange={handleInputChange}
                          className="w-full p-2 text-xs rounded-md border bg-background"
                        />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Registrar Signature URL
                      </label>
                      <input
                        type="url"
                        name="registrar_signature_url"
                        value={institution.registrar_signature_url || ""}
                        onChange={handleInputChange}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        Admission Letter Footer
                      </label>
                      <textarea
                        name="admission_letter_footer"
                        value={institution.admission_letter_footer || ""}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full p-2 text-xs rounded-md border bg-background"
                        placeholder="Official certification or footer text"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </SettingsPanel>
        )}

        {/* 2. Enrolment Governance Subsection */}
        {activeSection === "enrolment" && (
          <SettingsPanel
            id="enrolment"
            title="Enrolment Governance, Progression & Fees"
            subtitle="Global safeguards, registration windows, progression gates, late fee rules, and student reactivation charges."
            icon={ShieldCheck}
            badge="Policies & Fees"
            headerActions={settingsHeaderActions}
            onReset={() => handleResetSection("enrolment")}
          >
            <div className="space-y-6">
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">
                      Institution-wide reporting and unit-registration window
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      These dates apply across every department, programme, unit
                      and student for the active academic period only. Configure
                      another period after making it active in Calendar.
                    </p>
                  </div>
                  {activeEnrollmentContext?.academicYear &&
                  activeEnrollmentContext?.semester ? (
                    <span className="rounded-full border border-blue-200 bg-white px-3 py-1 text-[0.6875rem] font-semibold text-blue-800">
                      Active: {activeEnrollmentContext.academicYear.name} ·{" "}
                      {activeEnrollmentContext.semester.name}
                    </span>
                  ) : (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[0.6875rem] font-semibold text-amber-800">
                      No active academic period
                    </span>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                  {[
                    ["reporting_opens_at", "Reporting opens"],
                    ["reporting_closes_at", "Reporting closes"],
                    ["registration_opens_at", "Unit registration opens"],
                    ["registration_closes_at", "Unit registration closes"],
                    ["late_registration_closes_at", "Late grace closes"],
                  ].map(([name, label]) => (
                    <div key={name} className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        {label}
                      </label>
                      <input
                        type="date"
                        name={name}
                        value={institution[name] || ""}
                        onChange={handleInputChange}
                        disabled={!activeEnrollmentContext?.semester}
                        className="w-full rounded-md border bg-background p-2 text-xs disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                      />
                    </div>
                  ))}
                </div>
                {!activeEnrollmentContext?.semester && (
                  <p className="mt-3 text-xs font-medium text-amber-800">
                    Create and activate an academic year and semester in the
                    Calendar section before entering this window.
                  </p>
                )}
                {windowConfiguredForAnotherPeriod &&
                  activeEnrollmentContext?.semester && (
                    <p className="mt-3 text-xs font-medium text-amber-800">
                      The previous window belonged to another semester. Enter
                      the dates for the active period above and save to replace
                      its scope.
                    </p>
                  )}
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {[
                  [
                    "enrollment_controls_enabled",
                    "Enforce enrolment controls",
                    "Apply the configured dates and eligibility rules.",
                  ],
                  [
                    "require_configured_enrollment_windows",
                    "Require configured dates",
                    "Block activity when the institution-wide window dates are missing.",
                  ],
                  [
                    "require_intake_eligibility",
                    "Enforce intake eligibility",
                    "Future intakes cannot report early.",
                  ],
                  [
                    "allow_late_reporting",
                    "Allow late reporting",
                    "Permit reporting during the institution-wide late grace period.",
                  ],
                  [
                    "allow_late_unit_registration",
                    "Allow late unit registration",
                    "Permit unit registration during the institution-wide late grace period.",
                  ],
                ].map(([name, label, help]) => (
                  <label
                    key={name}
                    className="flex items-start gap-3 rounded-lg border p-3 bg-muted/20 hover:bg-muted/40 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      name={name}
                      checked={institution[name] !== false}
                      onChange={handleInputChange}
                      className="mt-0.5 h-4 w-4 rounded accent-primary"
                    />
                    <span>
                      <span className="block text-xs font-semibold text-foreground">
                        {label}
                      </span>
                      <span className="text-[0.6875rem] text-muted-foreground leading-snug block mt-0.5">
                        {help}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-foreground">
                    Progression gate
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    Set how many known failed units a student may carry into the
                    next module and the minimum attendance required. Pending or
                    unreleased marks are not treated as failures; attendance
                    below the configured threshold prevents progression.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {[
                    [
                      "max_formative_failed_units_for_progression",
                      "Maximum failed formative units",
                      "FA below 30/60",
                    ],
                    [
                      "max_summative_failed_units_for_progression",
                      "Maximum failed summative units",
                      "SA below 20/40",
                    ],
                    [
                      "minimum_attendance_percentage_for_progression",
                      "Minimum attendance for progression",
                      "AT% threshold",
                    ],
                  ].map(([name, label, help]) => (
                    <div key={name} className="space-y-1.5">
                      <label className="text-xs font-medium text-foreground">
                        {label}
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={
                          name ===
                          "minimum_attendance_percentage_for_progression"
                            ? "100"
                            : undefined
                        }
                        step={
                          name ===
                          "minimum_attendance_percentage_for_progression"
                            ? "0.01"
                            : "1"
                        }
                        inputMode="numeric"
                        name={name}
                        value={institution[name] ?? 0}
                        onChange={handleInputChange}
                        className="w-full rounded-md border bg-background p-2 text-xs"
                      />
                      <p className="text-[0.6875rem] text-muted-foreground">
                        {help}.{" "}
                        {name ===
                        "minimum_attendance_percentage_for_progression"
                          ? "Set 0 to disable the attendance gate."
                          : "Enter 0 to allow no failures."}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3 pt-2">
                {[
                  ["default_late_reporting_fee", "Late reporting fee"],
                  [
                    "default_late_unit_registration_fee",
                    "Late unit-registration fee",
                  ],
                  ["default_reactivation_fee", "Reactivation fee"],
                ].map(([name, label]) => (
                  <div key={name} className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      {label} (KES)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name={name}
                      value={institution[name] ?? 0}
                      onChange={handleInputChange}
                      disabled={!activeEnrollmentContext?.semester}
                      className="w-full p-2 text-xs rounded-md border bg-background disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                    />
                  </div>
                ))}
              </div>

              <p className="text-[0.6875rem] text-muted-foreground">
                Late charges are posted separately by Finance: late reporting,
                late unit-registration and reactivation.
              </p>
            </div>
          </SettingsPanel>
        )}

        {/* 3. Institution Calendar Subsection */}
        {activeSection === "calendar" && (
          <SystemCalendarSettings headerActions={settingsHeaderActions} />
        )}

        {/* 4. Automatic Timetable Rules Subsection */}
        {activeSection === "timetable" && (
          <SettingsPanel
            id="timetable"
            title="Automatic Timetable Rules"
            subtitle="Teaching rules are global; summative assessment dates and hours are saved separately for the active academic year and semester."
            icon={Clock}
            badge="Schedule Rules"
            headerActions={settingsHeaderActions}
            onReset={() => handleResetSection("timetable")}
          >
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Teaching day starts
                  </label>
                  <input
                    required
                    type="time"
                    name="timetable_day_starts_at"
                    value={String(
                      institution.timetable_day_starts_at || "07:00",
                    ).slice(0, 5)}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Teaching day ends
                  </label>
                  <input
                    required
                    type="time"
                    name="timetable_day_ends_at"
                    value={String(
                      institution.timetable_day_ends_at || "20:00",
                    ).slice(0, 5)}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Break starts (optional)
                  </label>
                  <input
                    type="time"
                    name="timetable_break_starts_at"
                    value={String(
                      institution.timetable_break_starts_at || "",
                    ).slice(0, 5)}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Break ends (optional)
                  </label>
                  <input
                    type="time"
                    name="timetable_break_ends_at"
                    value={String(
                      institution.timetable_break_ends_at || "",
                    ).slice(0, 5)}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                </div>
              </div>
              <p className="text-[0.6875rem] text-muted-foreground">
                Leave break times blank if teaching runs continuously without a
                global break.
              </p>

              <div className="space-y-4 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      Summative assessment window
                    </h3>
                    <p className="mt-1 text-[0.6875rem] leading-relaxed text-slate-600">
                      Only units with declared theory or practical assessment
                      hours can be placed on the timetable. The settings below
                      apply to the active academic period only.
                    </p>
                  </div>
                  {activeEnrollmentContext?.academicYear &&
                  activeEnrollmentContext?.semester ? (
                    <span className="rounded-full border border-indigo-200 bg-white px-2.5 py-1 text-[0.625rem] font-semibold text-indigo-800">
                      {activeEnrollmentContext.academicYear.name ||
                        activeEnrollmentContext.academicYear.code ||
                        activeEnrollmentContext.academicYear.label ||
                        "Active academic year"}{" "}
                      ·{" "}
                      {activeEnrollmentContext.semester.name ||
                        activeEnrollmentContext.semester.code ||
                        activeEnrollmentContext.semester.label ||
                        "Active semester"}
                    </span>
                  ) : (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[0.625rem] font-semibold text-amber-800">
                      Set an active academic period in Calendar
                    </span>
                  )}
                </div>
                {summativeTimetableLoading ? (
                  <p className="text-xs text-slate-500">
                    Loading assessment settings for the active period…
                  </p>
                ) : (
                  <>
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Exam day starts at
                        </label>
                        <input
                          required
                          type="time"
                          value={summativeTimetable.assessment_starts_at || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "assessment_starts_at",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Exam day ends at
                        </label>
                        <input
                          required
                          type="time"
                          value={summativeTimetable.assessment_ends_at || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "assessment_ends_at",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Assessment break starts
                        </label>
                        <input
                          type="time"
                          value={summativeTimetable.break_starts_at || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "break_starts_at",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Assessment break ends
                        </label>
                        <input
                          type="time"
                          value={summativeTimetable.break_ends_at || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "break_ends_at",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Exam period starts (date)
                        </label>
                        <input
                          required
                          type="date"
                          value={summativeTimetable.assessment_starts_on || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "assessment_starts_on",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Exam period ends (date)
                        </label>
                        <input
                          required
                          type="date"
                          value={summativeTimetable.assessment_ends_on || ""}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "assessment_ends_on",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-medium text-foreground">
                          Practical assessments start (date)
                        </label>
                        <input
                          required
                          type="date"
                          value={
                            summativeTimetable.practical_assessment_starts_on ||
                            summativeTimetable.assessment_starts_on ||
                            ""
                          }
                          min={summativeTimetable.assessment_starts_on || undefined}
                          max={summativeTimetable.assessment_ends_on || undefined}
                          onChange={(e) =>
                            handleSummativeTimetableChange(
                              "practical_assessment_starts_on",
                              e.target.value,
                            )
                          }
                          className="w-full rounded-md border bg-background p-2 text-xs"
                        />
                        <p className="text-[0.625rem] text-slate-500">
                          Practical blocks start on this date and use the
                          hourly start boundaries with their full declared
                          duration.
                        </p>
                      </div>
                    </div>
                    <div className="rounded-lg border border-indigo-100 bg-white px-3 py-2 text-[0.6875rem] text-slate-600">
                      <span className="font-semibold text-slate-900">
                        Hourly assessment grid:
                      </span>{" "}
                      the saved exam-day start and end times are divided into
                      one-hour columns. Each theory or practical assessment is
                      placed on an hourly start and spans its full declared
                      duration; no fixed examination sessions are required.
                    </div>
                    <p className="text-[0.6875rem] text-indigo-800">
                      Theory and practical assessments can be assigned different
                      dates in Exam Schedules. A scheduled slot must stay inside
                      these dates and outside the break. For each programme,
                      level and module, theory papers require a 60-minute
                      transition; only one practical is allowed per day, and a
                      practical day cannot also contain a theory paper.
                    </p>
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
                <div className="flex items-start gap-2">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                  <div>
                    <p className="text-xs font-semibold">
                      Automatic student rest safeguard
                    </p>
                    <p className="mt-0.5 text-[0.6875rem] leading-relaxed text-amber-800">
                      Auto-generation never schedules a programme level and
                      module for more than six continuous hours. A 60-minute gap
                      is required before its next lesson; authorised manual
                      edits may override this spacing.
                    </p>
                  </div>
                </div>
                <span className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[0.625rem] font-semibold text-amber-800">
                  6h maximum · 1h rest
                </span>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground block">
                  Active Teaching Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((label, index) => {
                    const day = index + 1;
                    const checked = (institution.timetable_working_days || [])
                      .map(Number)
                      .includes(day);
                    return (
                      <label
                        key={label}
                        className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${checked ? "border-primary bg-primary/10 text-primary font-bold" : "bg-background hover:bg-muted text-muted-foreground"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTimetableDay(day)}
                          className="h-3.5 w-3.5 accent-primary"
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </SettingsPanel>
        )}

        {/* 5. Finances & Billing Configuration Subsection */}
        {activeSection === "finances" && (
          <SettingsPanel
            id="finances"
            title="Finances & Billing Configuration"
            subtitle="Configure global fee rules, default billing modes, and statutory charges."
            icon={Building2}
            badge="Billing & Costs"
            headerActions={settingsHeaderActions}
            onReset={() => handleResetSection("finances")}
          >
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Billing System Mode
                  </label>
                  <select
                    name="billing_mode"
                    value={institution.billing_mode || "program_based"}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  >
                    <option value="program_based">
                      Program-Based (Tuition declared on Program)
                    </option>
                    <option value="unit_based">
                      Unit-Based (Tuition declared per Unit)
                    </option>
                  </select>
                  <p className="text-[0.625rem] text-muted-foreground mt-1">
                    Controls how student registration calculates base tuition
                    fees.
                  </p>
                </div>
                <div className="space-y-1.5 col-span-1 md:col-span-2">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <label className="text-xs font-bold text-foreground">
                        Statutory Fees Configuration
                      </label>
                      <p className="text-[0.625rem] text-muted-foreground mt-0.5">
                        Define statutory fees and select which modules they
                        apply to during billing.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleAddStatutoryFee}
                      className="h-7 px-3 text-[0.6875rem] font-semibold bg-secondary text-secondary-foreground rounded border hover:bg-secondary/80 flex items-center gap-1"
                    >
                      <span>+ Add Fee Item</span>
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(institution.statutory_fees_config || []).map(
                      (fee, idx) => (
                        <div
                          key={idx}
                          className="flex flex-wrap md:flex-nowrap items-center gap-3 p-3 border rounded-lg bg-muted/10"
                        >
                          <div className="flex-1 min-w-[200px] space-y-1">
                            <label className="text-[0.625rem] uppercase font-semibold text-muted-foreground">
                              Fee Name
                            </label>
                            <input
                              type="text"
                              value={fee.name}
                              onChange={(e) =>
                                handleUpdateStatutoryFee(
                                  idx,
                                  "name",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. Activity Fee"
                              className="w-full p-2 text-xs rounded-md border bg-background"
                            />
                          </div>
                          <div className="w-32 space-y-1">
                            <label className="text-[0.625rem] uppercase font-semibold text-muted-foreground">
                              Amount (KES)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={fee.amount}
                              onChange={(e) =>
                                handleUpdateStatutoryFee(
                                  idx,
                                  "amount",
                                  Number(e.target.value),
                                )
                              }
                              className="w-full p-2 text-xs rounded-md border bg-background"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[0.625rem] uppercase font-semibold text-muted-foreground">
                              Applies To
                            </label>
                            <div className="flex gap-2 items-center h-8">
                              {[1, 2, 3].map((m) => (
                                <label
                                  key={m}
                                  className="flex items-center gap-1 text-[0.6875rem] font-medium cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={(fee.modules || []).includes(m)}
                                    onChange={() =>
                                      handleToggleStatutoryFeeModule(idx, m)
                                    }
                                    className="rounded"
                                  />
                                  Mod {m}
                                </label>
                              ))}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveStatutoryFee(idx)}
                            className="mt-5 p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                            title="Remove fee"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ),
                    )}
                    {(!institution.statutory_fees_config ||
                      institution.statutory_fees_config.length === 0) && (
                      <div className="p-4 text-center border border-dashed rounded-lg text-xs text-muted-foreground">
                        No statutory fees defined. Click "Add Fee Item" to
                        start.
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Tuition Fee Default (KES)
                  </label>
                  <input
                    type="number"
                    name="default_school_fee"
                    value={institution.default_school_fee || 0}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                  <p className="text-[0.625rem] text-muted-foreground mt-1">
                    Used as the tuition default when no programme, level,
                    module, or unit tuition is configured.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Attachment Fee (KES)
                  </label>
                  <input
                    type="number"
                    name="attachment_fee"
                    value={institution.attachment_fee || 0}
                    onChange={handleInputChange}
                    className="w-full p-2 text-xs rounded-md border bg-background"
                  />
                  <p className="text-[0.625rem] text-muted-foreground mt-1">
                    Fee charged during the last module.
                  </p>
                </div>
              </div>

              {institution.billing_mode === "unit_based" && (
                <div className="border-t pt-4 mt-4">
                  <h4 className="text-sm font-semibold mb-3">
                    Unit-based Tuition Fees
                  </h4>
                  <p className="text-[0.625rem] text-muted-foreground mb-3">
                    Set the default tuition charged for each registered unit
                    classification.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {UNIT_TYPES.map((type) => (
                      <div
                        key={type}
                        className="border p-3 rounded-lg bg-muted/20"
                      >
                        <label className="text-sm font-bold capitalize mb-2 block">
                          {type} Units
                        </label>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-medium text-muted-foreground">
                            Tuition Fee
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-[0.625rem] text-muted-foreground font-semibold">
                              KES
                            </span>
                            <input
                              type="number"
                              min="0"
                              className="w-24 text-xs p-1.5 border rounded focus:ring-1 focus:ring-primary focus:outline-none"
                              value={unitTypeFees[type]?.tuition ?? ""}
                              placeholder="0.00"
                              onChange={(e) =>
                                handleUnitTypeFeeChange(
                                  type,
                                  "tuition",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold">
                  Summative Examination Fees
                </h4>
                <p className="mb-3 mt-1 text-[0.625rem] text-muted-foreground">
                  The saved price is charged per unit when a student books a
                  summative examination. It applies in both programme-based and
                  unit-based tuition modes.
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  {UNIT_TYPES.map((type) => (
                    <div
                      key={type}
                      className="rounded-lg border bg-muted/20 p-3"
                    >
                      <label className="mb-3 block text-sm font-bold capitalize">
                        {type} Examination
                      </label>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs font-medium text-muted-foreground">
                          Price per unit
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-[0.625rem] font-semibold text-muted-foreground">
                            KES
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-28 rounded border p-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                            value={unitTypeFees[type]?.exam ?? ""}
                            placeholder="0.00"
                            onChange={(event) =>
                              handleUnitTypeFeeChange(
                                type,
                                "exam",
                                event.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold">
                      Summative examination booking window
                    </h4>
                    <p className="mb-3 mt-1 text-[0.625rem] text-muted-foreground">
                      Set this independently for each academic year and calendar
                      semester. Dates start at midnight; the closing date
                      remains available until midnight after that date. Resit
                      bookings use the same period window.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveBookingWindow()}
                    disabled={
                      bookingWindowSaving ||
                      bookingWindowLoading ||
                      !bookingWindowPeriod.semesterId
                    }
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Save className="h-3.5 w-3.5" />
                    {bookingWindowSaving ? "Saving…" : "Save period window"}
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      Academic year
                    </label>
                    <select
                      value={bookingWindowPeriod.academicYearId}
                      onChange={handleBookingYearChange}
                      className="w-full rounded-md border bg-background p-2 text-xs"
                    >
                      <option value="">Select academic year</option>
                      {[
                        ...new Map(
                          bookingPeriods.map((period) => [
                            period.year.id,
                            period.year,
                          ]),
                        ).values(),
                      ].map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      Calendar semester
                    </label>
                    <select
                      value={bookingWindowPeriod.semesterId}
                      onChange={handleBookingSemesterChange}
                      disabled={!bookingWindowPeriod.academicYearId}
                      className="w-full rounded-md border bg-background p-2 text-xs disabled:cursor-not-allowed disabled:bg-slate-100"
                    >
                      <option value="">Select semester</option>
                      {bookingPeriods
                        .filter(
                          (period) =>
                            period.year.id ===
                            bookingWindowPeriod.academicYearId,
                        )
                        .map((period) => (
                          <option
                            key={period.semester.id}
                            value={period.semester.id}
                          >
                            {period.semester.name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                {bookingWindowPeriod.semesterId && (
                  <p className="mt-2 text-[0.625rem] font-medium text-primary">
                    Applies to:{" "}
                    {bookingPeriods.find(
                      (period) =>
                        period.semester.id === bookingWindowPeriod.semesterId,
                    )?.year?.name || "Selected academic year"}{" "}
                    ·{" "}
                    {bookingPeriods.find(
                      (period) =>
                        period.semester.id === bookingWindowPeriod.semesterId,
                    )?.semester?.name || "Selected semester"}
                  </p>
                )}
                <label className="mt-3 flex items-center gap-2 text-xs font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={bookingWindow.booking_enabled !== false}
                    onChange={(event) =>
                      handleBookingWindowChange(
                        "booking_enabled",
                        event.target.checked,
                      )
                    }
                    className="h-3.5 w-3.5 rounded accent-primary"
                  />
                  Allow summative examination and resit booking
                </label>
                <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      Booking opens (date)
                    </label>
                    <input
                      type="date"
                      value={bookingWindow.opens_at || ""}
                      onChange={(event) =>
                        handleBookingWindowChange(
                          "opens_at",
                          event.target.value,
                        )
                      }
                      disabled={bookingWindow.booking_enabled === false}
                      className="w-full rounded-md border bg-background p-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">
                      Booking closes (date)
                    </label>
                    <input
                      type="date"
                      value={bookingWindow.closes_at || ""}
                      onChange={(event) =>
                        handleBookingWindowChange(
                          "closes_at",
                          event.target.value,
                        )
                      }
                      disabled={bookingWindow.booking_enabled === false}
                      className="w-full rounded-md border bg-background p-2 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>
                </div>
                <div
                  className={`mt-3 rounded-lg border px-3 py-2 text-xs ${bookingWindow.booking_enabled === false ? "border-rose-200 bg-rose-50 text-rose-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
                >
                  {bookingWindowLoading
                    ? "Loading the selected period window…"
                    : !bookingWindowPeriod.semesterId
                      ? "Create an academic year and semester in Calendar before configuring booking."
                      : bookingWindow.booking_enabled === false
                        ? "Booking is closed for this academic period."
                        : bookingWindow.opens_at || bookingWindow.closes_at
                          ? "Booking is limited to the configured window for this academic period."
                          : "Continuous booking is enabled for this academic period."}
                </div>
              </div>
            </div>
          </SettingsPanel>
        )}

        {/* 6. Theme & Branding Configuration Subsection */}
        {activeSection === "theme" && (
          <SettingsPanel
            id="theme"
            title="Theme & Branding Configuration"
            subtitle="Customize application colors, typography font families, and global layout scaling."
            icon={Palette}
            badge="Branding & Look"
            headerActions={settingsHeaderActions}
            onReset={() => handleResetSection("theme")}
          >
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {/* Primary Color */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={theme.primary}
                      onChange={(e) =>
                        handleThemeChange("primary", e.target.value)
                      }
                      className="h-9 w-16 p-1 rounded-md border cursor-pointer bg-background"
                    />
                    <span className="text-xs font-mono bg-muted px-2.5 py-1 rounded border font-semibold">
                      {theme.primary}
                    </span>
                  </div>
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Navbar, action buttons, active badges
                  </p>
                </div>

                {/* Secondary Color */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={theme.secondary}
                      onChange={(e) =>
                        handleThemeChange("secondary", e.target.value)
                      }
                      className="h-9 w-16 p-1 rounded-md border cursor-pointer bg-background"
                    />
                    <span className="text-xs font-mono bg-muted px-2.5 py-1 rounded border font-semibold">
                      {theme.secondary}
                    </span>
                  </div>
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Secondary UI components & card headers
                  </p>
                </div>

                {/* Accent Color */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Accent Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={theme.accent}
                      onChange={(e) =>
                        handleThemeChange("accent", e.target.value)
                      }
                      className="h-9 w-16 p-1 rounded-md border cursor-pointer bg-background"
                    />
                    <span className="text-xs font-mono bg-muted px-2.5 py-1 rounded border font-semibold">
                      {theme.accent}
                    </span>
                  </div>
                  <p className="text-[0.6875rem] text-muted-foreground">
                    Highlights and focus indicators
                  </p>
                </div>

                {/* Font Family */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Font Family
                  </label>
                  <select
                    value={theme.fontFamily}
                    onChange={(e) =>
                      handleThemeChange("fontFamily", e.target.value)
                    }
                    className="w-full h-9 px-3 text-xs rounded-md border bg-background"
                  >
                    <option value="Inter">Inter (Default)</option>
                    <option value="Roboto">Roboto</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Lato">Lato</option>
                    <option value="Montserrat">Montserrat</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Arial">Arial</option>
                    <option value="Times New Roman">Times New Roman</option>
                  </select>
                </div>

                {/* Font Size */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-foreground">
                    Base Font Size
                  </label>
                  <select
                    value={theme.fontSize}
                    onChange={(e) =>
                      handleThemeChange("fontSize", e.target.value)
                    }
                    className="w-full h-9 px-3 text-xs rounded-md border bg-background"
                  >
                    <option value="12px">XS (12px)</option>
                    <option value="14px">Small (14px)</option>
                    <option value="16px">Medium (16px - Default)</option>
                    <option value="18px">Large (18px)</option>
                    <option value="20px">XL (20px)</option>
                  </select>
                </div>
              </div>

              {/* Theme Live Preview */}
              <div className="grid gap-4 md:grid-cols-2 pt-2">
                <div className="rounded-lg border p-4 bg-muted/10 space-y-2">
                  <h4 className="text-xs font-bold text-foreground">
                    Live Button Preview
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <button className="px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-md shadow-2xs">
                      Primary Button
                    </button>
                    <button className="px-3 py-1.5 text-xs font-semibold bg-secondary text-secondary-foreground rounded-md shadow-2xs">
                      Secondary Button
                    </button>
                    <button className="px-3 py-1.5 text-xs font-semibold border bg-background hover:bg-accent hover:text-accent-foreground rounded-md shadow-2xs">
                      Outline Button
                    </button>
                  </div>
                </div>

                <div className="rounded-lg border p-4 bg-muted/10 space-y-2">
                  <h4 className="text-xs font-bold text-foreground">
                    Live Alert Card Preview
                  </h4>
                  <div className="p-3 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                    This is a primary themed live preview alert card.
                  </div>
                </div>
              </div>

              <p className="border-t pt-4 text-xs text-slate-500">
                Changes preview immediately. Use <b>Save appearance</b> in the
                page header to publish them for all users.
              </p>
            </div>
          </SettingsPanel>
        )}
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
